<template>
  <div class="bid-audit-report">
    <div class="d-flex align-center justify-space-between mb-4 no-print">
      <div>
        <h3 class="text-h6 font-weight-bold theme-heading mb-0">
          Staffing Audit Report
        </h3>
        <p class="text-caption grey--text mb-0">
          {{ peopleCount }} {{ peopleCount === 1 ? "person" : "people" }} scored
          against {{ coverage.length }}
          {{ coverage.length === 1 ? "role" : "roles" }}
          <span v-if="auditResult.executionTimeMs">
            · {{ auditResult.executionTimeMs }}ms
          </span>
        </p>
      </div>
      <div class="d-flex" style="gap: 8px">
        <v-btn small outlined color="#007AFF" @click="$emit('export')">
          <v-icon small left>mdi-printer</v-icon>
          Export report
        </v-btn>
        <v-btn
          small
          outlined
          color="#10B981"
          :loading="isAuditing"
          @click="$emit('rerun')"
        >
          <v-icon small left>mdi-refresh</v-icon>
          Re-run audit
        </v-btn>
        <v-btn small text color="grey darken-1" @click="$emit('start-over')">
          <v-icon small left>mdi-restart</v-icon>
          Start over
        </v-btn>
      </div>
    </div>

    <!-- View state banner (set by Sage re_rank_audit "Apply view"). Affects
         ordering / filtering of the displayed people only — never any score. -->
    <v-alert
      v-if="viewState && viewState.explanation"
      dense
      text
      color="#8b5cf6"
      class="mb-4 no-print"
    >
      <div class="d-flex align-center justify-space-between">
        <div class="text-body-2">
          <v-icon small color="#8b5cf6" class="mr-1">mdi-eye-outline</v-icon>
          View applied: {{ viewState.explanation }}
        </div>
        <v-btn x-small text color="#8b5cf6" @click="$emit('clear-view')">
          Clear view
        </v-btn>
      </div>
    </v-alert>

    <!-- 1. Per-person fit -->
    <h4 class="text-subtitle-1 font-weight-bold mb-3 theme-heading">
      Per-person fit
    </h4>
    <div
      v-for="person in displayedPeople"
      :key="person.match ? person.match.profileId : person.assignedRoleId"
      class="person-audit-block mb-4"
    >
      <div class="d-flex align-center flex-wrap mb-1" style="gap: 8px">
        <v-chip small label color="#EEF2FF" text-color="#4338CA">
          <v-icon x-small left>mdi-briefcase-outline</v-icon>
          {{ person.assignedRoleTitle || "Unassigned role" }}
        </v-chip>
        <v-chip
          small
          :color="verdictColor(person.fitVerdict)"
          text-color="white"
          class="font-weight-bold"
        >
          {{ verdictLabel(person.fitVerdict) }} fit · {{ person.fitScore }}%
        </v-chip>
        <v-chip
          v-if="person.misassignedHint"
          small
          outlined
          color="#F59E0B"
        >
          <v-icon x-small left>mdi-swap-horizontal</v-icon>
          Better fit for: {{ person.misassignedHintTitle }}
        </v-chip>
      </div>

      <JDMatchCard
        v-if="person.match"
        :result="person.match"
        :job-title="person.assignedRoleTitle || 'Assigned role'"
        @view-profile="$emit('view-profile', $event)"
      />
    </div>

    <v-divider class="my-5" />

    <!-- 2. Team coverage heatmap -->
    <h4 class="text-subtitle-1 font-weight-bold mb-3 theme-heading">
      Team coverage
    </h4>
    <div
      v-for="role in coverage"
      :key="role.roleId"
      class="coverage-card pa-4 mb-3"
    >
      <div class="d-flex align-center justify-space-between mb-2">
        <span class="text-subtitle-2 font-weight-bold theme-heading">
          {{ role.roleTitle }}
        </span>
        <span
          class="text-body-2 font-weight-bold"
          :style="{ color: coverageColor(role.coveragePct) }"
        >
          {{ Math.round(role.coveragePct || 0) }}% covered
        </span>
      </div>
      <v-progress-linear
        :value="role.coveragePct || 0"
        :color="coverageColor(role.coveragePct)"
        height="8"
        rounded
        class="mb-3"
      />

      <div
        v-if="role.coveredRequirements && role.coveredRequirements.length > 0"
        class="mb-2"
      >
        <div class="text-caption grey--text mb-1">Covered</div>
        <div class="d-flex flex-wrap" style="gap: 6px">
          <v-chip
            v-for="(req, cIdx) in role.coveredRequirements"
            :key="'cov-' + cIdx"
            x-small
            color="#E8F5E9"
            text-color="#10B981"
          >
            <v-icon x-small left>mdi-check</v-icon>
            {{ req }}
          </v-chip>
        </div>
      </div>

      <div v-if="role.gapRequirements && role.gapRequirements.length > 0">
        <div class="text-caption grey--text mb-1">Gaps</div>
        <div class="d-flex flex-wrap" style="gap: 6px">
          <v-chip
            v-for="(req, gIdx) in role.gapRequirements"
            :key="'gap-' + gIdx"
            x-small
            color="#FDECEA"
            text-color="#EF4444"
          >
            <v-icon x-small left>mdi-close</v-icon>
            {{ req }}
          </v-chip>
        </div>
      </div>
    </div>

    <!-- 3. Gaps -->
    <div v-if="gaps.length > 0" class="mt-5">
      <h4 class="text-subtitle-1 font-weight-bold mb-3 theme-heading">
        Gaps to watch
      </h4>
      <div class="gaps-card pa-4">
        <div
          v-for="(gap, gIdx) in gaps"
          :key="'g-' + gIdx"
          class="d-flex align-start mb-2"
        >
          <v-icon small color="#F59E0B" class="mr-2 mt-1">
            mdi-alert-circle-outline
          </v-icon>
          <span class="text-body-2">{{ gap }}</span>
        </div>
      </div>
    </div>

    <!-- 4. Swap recommendations -->
    <div v-if="swaps.length > 0" class="mt-5">
      <h4 class="text-subtitle-1 font-weight-bold mb-3 theme-heading">
        Swap recommendations
      </h4>
      <div
        v-for="(swap, sIdx) in swaps"
        :key="'swap-' + sIdx"
        class="swap-card pa-4 mb-3"
      >
        <div class="d-flex align-center justify-space-between flex-wrap mb-2" style="gap: 8px">
          <div class="text-body-2">
            <span class="font-weight-bold theme-heading">
              For {{ swap.forRoleTitle }}:
            </span>
            consider
            <span class="font-weight-bold" style="color: #007AFF">
              {{ swap.candidate ? swap.candidate.name : "a candidate" }}
            </span>
            <span v-if="swap.replacesProfileId" class="grey--text">
              instead of {{ replacedName(swap.replacesProfileId) }}
            </span>
          </div>
          <v-btn
            x-small
            color="#10B981"
            dark
            class="no-print"
            @click="$emit('add-swap', swap)"
          >
            <v-icon x-small left>mdi-plus</v-icon>
            Add to team
          </v-btn>
        </div>
        <div
          v-if="swap.candidate && swap.candidate.currentTitle"
          class="text-caption grey--text mb-2"
        >
          {{ swap.candidate.currentTitle }}
          <span v-if="swap.candidate.currentCompany">
            at {{ swap.candidate.currentCompany }}</span
          >
          <span v-if="swap.candidate.overallScore != null">
            · {{ swap.candidate.overallScore }}% on this role</span
          >
        </div>
        <div v-if="swap.reason" class="swap-reason pa-2 text-caption">
          <v-icon x-small color="#6366F1" class="mr-1">mdi-text-search</v-icon>
          {{ swap.reason }}
        </div>
      </div>
    </div>

    <!-- 5. Decision-authority disclaimer -->
    <div class="ai-disclaimer mt-5">
      <v-icon small color="#9e9e9e" class="mr-1">mdi-scale-balance</v-icon>
      <span>
        This is an audit tool. Final staffing decisions rest with your team. Use
        alongside interviews, manager input, and domain knowledge.
      </span>
    </div>
  </div>
</template>

<script>
import JDMatchCard from "../Intelligence/JDMatchCard.vue";

export default {
  name: "BidAuditReport",
  components: { JDMatchCard },
  props: {
    auditResult: { type: Object, required: true },
    selectedProfiles: { type: Array, default: () => [] },
    isAuditing: { type: Boolean, default: false },
    // Display-only view state from Sage re_rank_audit. Shape:
    // { sort?: "coverage"|"fit", filter?: "weak", explanation: string }
    viewState: { type: Object, default: null },
  },
  computed: {
    perPerson() {
      return this.auditResult.perPerson || [];
    },
    coverage() {
      return this.auditResult.teamCoverage || [];
    },
    gaps() {
      return this.auditResult.gaps || [];
    },
    swaps() {
      return this.auditResult.swapRecommendations || [];
    },
    peopleCount() {
      return this.perPerson.length;
    },
    // displayedPeople applies the Sage-driven view state. It ONLY filters or
    // reorders — it never recomputes or alters any fitScore.
    displayedPeople() {
      let list = this.perPerson.slice();
      const view = this.viewState || {};
      if (view.filter === "weak") {
        list = list.filter((p) => p.fitVerdict === "weak");
      } else if (view.filter === "misassigned") {
        list = list.filter((p) => !!p.misassignedHint);
      }
      if (view.sort === "fit") {
        list.sort((a, b) => (a.fitScore || 0) - (b.fitScore || 0));
      } else if (view.sort === "coverage") {
        const order = { weak: 0, adequate: 1, strong: 2 };
        list.sort(
          (a, b) =>
            (order[a.fitVerdict] || 0) - (order[b.fitVerdict] || 0),
        );
      }
      return list;
    },
  },
  methods: {
    verdictColor(verdict) {
      if (verdict === "strong") return "#10B981";
      if (verdict === "adequate") return "#F59E0B";
      return "#EF4444";
    },
    verdictLabel(verdict) {
      if (verdict === "strong") return "Strong";
      if (verdict === "adequate") return "Adequate";
      return "Weak";
    },
    coverageColor(pct) {
      const v = pct || 0;
      if (v >= 70) return "#10B981";
      if (v >= 40) return "#F59E0B";
      return "#EF4444";
    },
    replacedName(profileId) {
      const found = this.selectedProfiles.find(
        (p) => p.profileId === profileId,
      );
      if (found) return found.name;
      const match = this.perPerson.find(
        (p) => p.match && p.match.profileId === profileId,
      );
      return match && match.match ? match.match.name : "the assigned person";
    },
  },
};
</script>

<style scoped>
.coverage-card {
  background: #fafafa;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
}
.gaps-card {
  background: #fff8e1;
  border: 1px solid #f59e0b;
  border-radius: 8px;
}
.swap-card {
  background: #f5f7fa;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
}
.swap-reason {
  background: rgba(99, 102, 241, 0.06);
  border-left: 3px solid #6366f1;
  border-radius: 4px;
  line-height: 1.5;
}
.ai-disclaimer {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  padding: 10px 14px;
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.03);
  border: 1px solid #e0e0e0;
  font-size: 11px;
  color: #9e9e9e;
  line-height: 1.5;
  font-style: italic;
}
</style>

<style>
.theme--dark .bid-audit-report .coverage-card,
.theme--dark .bid-audit-report .swap-card {
  background: #2a2a3e;
  border-color: #444;
}
.theme--dark .bid-audit-report .gaps-card {
  background: rgba(245, 158, 11, 0.1);
}
.theme--dark .bid-audit-report .ai-disclaimer {
  background: rgba(255, 255, 255, 0.05);
  border-color: #444;
  color: #aaa;
}
@media print {
  .no-print {
    display: none !important;
  }
}
</style>
